import { Dispatch, SetStateAction, useEffect, useState } from "react";
import headerImage from "assets/image/banner-placeholder.png";
import {
  ArrowUpRightIcon,
  CalendarIconMain,
  CircleFilledIcon,
  FacebookFilledIcon,
  GlobeFilledIcon,
  InstagramFilledIcon,
  LinkedInFilledIcon,
  LocationIcon,
  PlusIcon,
  StarEmptyIcon,
  StarFilledIcon,
  StarHalfIcon,
  TargetIcon,
  TicketStarIcon,
  UserIconThinking,
  VerticalDividerIcon,
  YoutubeFilledIcon,
  XTwitterIcon,
  MoneyReceiveIcon,
} from "assets/icons";
import { CircleFlag } from "react-circle-flags";
import { FetchedBookings } from "interfaces/Bookings";
import { Speaker } from "interfaces/ExternalPage/SpeakerLandingPage.interface";
import { Drawer, Popover } from "antd";
import { openInNewTab } from "utils/openInNewTab";
import getPublicProfileUrl from "utils/getPublicProfileUrl";
// import { useGetDisplayPicture } from "hooks/query/useSettings";
import { getCountryCode } from "utils/countryList";
import { imageStreamPublic } from "utils/imageStream";
import UserImage from "components/UI/Avatar/UserImage";
import InviteSpeakerDrawer from "./InviteSpeakerDrawer";
import { getEventTerminology } from "utils/eventTerminology";
// import { getUserDetails } from "utils/localStorageServices";

// Interface for resolved image with URL
interface ResolvedImage {
  id: string;
  url: string;
}

export default function NewHeroSection({
  speaker,
  booking,
  userName,
  isUserLoggedIn,
  isPageOwner,
  handleOpenLoginModal,
  setOpenInviteDrawer,
  openInviteDrawer,
}: {
  speaker: Speaker;
  booking: FetchedBookings | null;
  userName?: string;
  isUserLoggedIn: boolean;
  isPageOwner: boolean;
  handleOpenLoginModal: () => void;
  setOpenInviteDrawer: Dispatch<SetStateAction<boolean>>;
  openInviteDrawer: boolean;
}) {
  const { singular: itemLabel, plural: pluralItemLabel } =
    getEventTerminology();
  const [resolvedImages, setResolvedImages] = useState<ResolvedImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  // const [openInviteDrawer, setOpenInviteDrawer] = useState(false);

  // Fetch image URLs from IDs
  useEffect(() => {
    const loadImages = async () => {
      if (!speaker.images || speaker.images.length === 0) {
        setImagesLoading(false);
        return;
      }

      setImagesLoading(true);
      try {
        const imagePromises = speaker.images.map(async (id) => {
          const url = await imageStreamPublic(id);
          return {
            id,
            url: url ?? "",
          };
        });

        const images = await Promise.all(imagePromises);
        // Filter out any images that failed to load
        setResolvedImages(images.filter((img) => img.url));
      } catch (error) {
        console.error("Failed to load speaker images:", error);
      } finally {
        setImagesLoading(false);
      }
    };

    loadImages();
  }, [speaker.images]);

  // Get available countries from speaker data, fallback to empty array
  const availableCountries = speaker.availableCountries || [];
  const isWorldwide =
    availableCountries.length > 0 &&
    availableCountries.every(
      (c) => c?.toLowerCase().replace(/\.$/, "") === "worldwide",
    );

  // Popover content showing all available countries
  const content = (
    <div className="min-w-[458px] bg-[#F2ECE2] p-6 rounded-[20px]">
      <div className="flex gap-2 items-center">
        <CircleFilledIcon />
        <p className="text-[#545454] text-base font-monaMedium">
          Available to speak in:
        </p>
      </div>

      {/* Display all available countries in a grid */}
      <div className="grid grid-cols-3 gap-4 mt-[33px]">
        {availableCountries.map((countryName, index) => (
          <div
            key={index}
            className="col-span-1 flex items-center gap-1 p-1 bg-[#EDE7DD] rounded-[16px] border border-[#E8E5DE]"
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <CircleFlag
                countryCode={getCountryCode(countryName)}
                height="32"
              />
            </div>
            <p className="text-base font-monaMedium text-[#545454]">
              {countryName}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <section className="w-full mt-[27.67px]">
        <section className="w-full rounded-[20px] overflow-hidden">
          {/* Header image */}
          {imagesLoading ? (
            <div className="w-full h-[96px] sm:h-[194px] object-cover bg-[#E0D7CA] animate-pulse"></div>
          ) : speaker.images &&
            speaker.images.length > 0 &&
            resolvedImages.length === 0 ? (
            // Initial loading skeleton for the whole section
            <div
              className="w-full h-[96px] sm:h-[194px] object-cover"
              style={{
                backgroundImage: `url(${headerImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            ></div>
          ) : (
            <div
              className="w-full h-[96px] sm:h-[194px] object-cover"
              style={{
                backgroundImage: `url(${
                  (resolvedImages?.[0]?.url as string) || headerImage
                })`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            ></div>
          )}

          {/* Main content */}
          <div className="relative flex gap-[48px] w-full lg:bg-[#F7F0E8] lg:px-[36px] sm:pt-[19.69px] h-fit min-h-[250px] pb-7">
            {/* Profile section copy but hidden */}
            <div className="w-[243px] min-w-[243px] h-[252px] border-[3px] border-[#F2ECE2] opacity-0 hidden lg:block"></div>
            {/* Profile image */}
            <div className="absolute top-[-38px] sm:-top-[87px] left-[15px] sm:left-[36px]">
              <UserImage
                userId={userName || ""}
                params={{ speakerProfile: true }}
                rounded="3xl"
                className="w-[90px] h-[90px] sm:w-[243px] sm:h-[252px] border-[3px] border-[#F2ECE2] bg-[#FDF6EE]"
              />
              {/* Rating and review */}
              <div className="hidden lg:block">
                <Rating
                  rating={speaker.rating}
                  reviewCount={speaker.verifiedRating}
                />
              </div>
            </div>
            {/* Speaker info */}
            <div className="">
              <h1 className="text-[20px] md:text-[32px] font-monaMedium text-black mt-[64px] sm:mt-[150px] lg:mt-0">
                {speaker.name}
              </h1>
              <p className="text-base sm:text-lg font-monaRegular sm:font-monaMedium text-[#545454] mt-1 sm:mt-0">
                {speaker.designation}
              </p>
              <div
                className={`${!speaker.name && !speaker.designation ? "mt-10" : ""} mt-5 py-[6px] border border-[#E8E5DE] rounded-lg flex items-center px-3 gap-3 w-fit`}
              >
                <Stats
                  icon={TicketStarIcon}
                  value={speaker?.events.toString()}
                  label={pluralItemLabel}
                />
                <VerticalDividerIcon />
                <Stats
                  icon={UserIconThinking}
                  value={speaker?.attendees.toString()}
                  label="attendees"
                />
                <VerticalDividerIcon />
                <Stats
                  icon={TargetIcon}
                  value={`${speaker?.engagementScore}%`}
                  label="engagement score"
                />
              </div>
              <div className="mt-5 flex items-center gap-8 flex-wrap">
                {/* Price Range - Only show if amounts are set */}
                <div
                  className={`flex gap-x-2 items-center h-fit ${
                    speaker.getQuote ||
                    (speaker.minimumAmount === 0 && speaker.maximumAmount === 0)
                      ? "hidden"
                      : ""
                  }`}
                >
                  <MoneyReceiveIcon />
                  <p className="text-[#333333] text-lg font-sfMedium">
                    From {speaker.minimumAmount.toLocaleString()}{" "}
                    <sup>{speaker.currency || "$"}</sup>
                  </p>
                </div>
                {/*  */}
                <div className="flex gap-x-2 items-center">
                  <LocationIcon />
                  <p className="text-sm text-[#545454] font-monaRegular md:text-base md:font-monaMedium">
                    Based in:
                  </p>
                  <div className="p-1 h-[28px] bg-[#EDE7DD] rounded-[16px] flex items-center gap-1 border border-[#E8E5DE]">
                    <div className="w-5">
                      <CircleFlag
                        countryCode={getCountryCode(speaker.location)}
                        height="18"
                      />
                    </div>
                    <p className="text-[#545454] text-base font-monaMedium">
                      {speaker.location}
                    </p>
                  </div>
                </div>

                {/* Available Countries - Only show if countries are set */}
                {availableCountries && availableCountries.length > 0 && (
                  <div className="flex gap-3 items-center">
                    <div className="flex gap-2 items-center">
                      <CircleFilledIcon />
                      <p className="text-sm text-[#545454] font-monaRegular md:text-base md:font-monaMedium">
                        {isWorldwide
                          ? "Available to speak:"
                          : "Available to speak in:"}
                      </p>
                    </div>

                    {isWorldwide ? (
                      <div className="flex items-center gap-1.5 bg-[#EDE7DD] pl-1 pr-3 py-1 rounded-[16px] border border-[#E8E5DE]">
                        <span className="w-5 h-5 flex items-center justify-center text-[#545454]">
                          <GlobeFilledIcon />
                        </span>
                        <p className="text-[#545454] text-sm md:text-base font-monaMedium leading-none">
                          Worldwide
                        </p>
                      </div>
                    ) : (
                      <Popover
                        content={content}
                        arrow={false}
                        placement="bottom"
                        trigger={"click"}
                        styles={{
                          body: {
                            padding: 0,
                            overflow: "hidden",
                            borderRadius: "20px",
                          },
                        }}
                      >
                        <button className="flex items-center gap-1 bg-[#EDE7DD] p-1 rounded-[16px] border border-[#E8E5DE] hover:bg-[#E3D9C8] transition-colors cursor-pointer">
                          {availableCountries
                            .slice(0, 3)
                            .map((countryName, index) => (
                              <div key={index} className="w-5">
                                <CircleFlag
                                  countryCode={getCountryCode(countryName)}
                                  height="18"
                                />
                              </div>
                            ))}
                          {availableCountries.length > 3 && (
                            <p className="text-[#545454] text-base font-monaMedium">
                              +{availableCountries.length - 3}
                            </p>
                          )}
                        </button>
                      </Popover>
                    )}
                  </div>
                )}
              </div>
              {/* Call to action buttons */}
              <div className="mt-5 flex gap-3">
                <button
                  className="h-10 w-[50%] md:w-fit rounded-lg bg-[#E3592E] border border-[#EFE9DF] text-base md:text-lg font-monaMedium text-[#F6F3EF] flex items-center px-2 md:pl-3 md:pr-2 gap-2 hover:bg-primary"
                  onClick={() => {
                    if (userName) {
                      openInNewTab(getPublicProfileUrl(userName, "/book"));
                    }
                  }}
                >
                  <CalendarIconMain stroke="#F6F3EF" />
                  Book a session
                </button>

                {speaker.canBeInvited && (
                  <button
                    disabled={isPageOwner}
                    onClick={() => {
                      if (isPageOwner) return;
                      if (isUserLoggedIn) {
                        setOpenInviteDrawer(true);
                      } else {
                        handleOpenLoginModal();
                      }
                    }}
                    className={`h-10 w-[50%] md:w-fit rounded-lg border border-[#E3592E] text-base md:text-lg font-monaMedium text-[#E3592E] flex items-center px-2 md:pl-3 md:pr-2 gap-2 ${isPageOwner ? "opacity-50 cursor-not-allowed" : "hover:bg-primary hover:text-[#F6F3EF]"}`}
                  >
                    <PlusIcon stroke="currentColor" height={20} width={20} />
                    Invite to {itemLabel}
                  </button>
                )}
              </div>

              {/*  */}
              <div className=" absolute top-0 right-0 pt-[14px] sm:pt-[19.67px] sm:pr-[32px]">
                {/* Socials */}
                <div className="hidden lg:block">
                  <SocialIcons socials={speaker.socials} />
                </div>
                {/* Rating */}
                <div className="lg:hidden">
                  <Rating
                    rating={speaker.rating}
                    reviewCount={speaker.verifiedRating}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* socials for small screen */}
        <div className="w-full flex flex-col gap-3 md:hidden mt-8">
          {speaker.socials.map((social, index) => (
            <Socials key={index} platform={social.platform} url={social.url} />
          ))}
        </div>
      </section>

      {/* Invite Speaker Drawer */}
      <Drawer
        placement="right"
        closable={false}
        onClose={() => setOpenInviteDrawer(false)}
        open={openInviteDrawer}
        width={468}
        destroyOnClose
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <InviteSpeakerDrawer
          open={openInviteDrawer}
          onClose={() => setOpenInviteDrawer(false)}
          speaker={speaker}
        />
      </Drawer>
    </>
  );
}

function Stats({
  icon: Icon,
  label,
  value,
}: {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  value: string;
  label: string;
}) {
  return (
    <div className="flex gap-2  flex-col md:flex-row items-start md:items-center">
      <Icon />
      <p className="text-[#545454] font-monaRegular text-lg">
        {value} <span className="text-base">{label}</span>
      </p>
    </div>
  );
}

export function Rating({
  rating = 0,
  reviewCount = 0,
}: {
  rating?: number;
  reviewCount?: number;
}) {
  const renderStars = () => {
    const stars = [];
    const roundedRating = Math.round(rating * 10) / 10;
    const fullStars = Math.floor(roundedRating);
    const hasHalfStar = roundedRating % 1 >= 0.1 && roundedRating % 1 < 1;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    // Render filled stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <StarFilledIcon key={`filled-${i}`} width={17.8} height={17.8} />,
      );
    }

    // Render half star if applicable
    if (hasHalfStar) {
      stars.push(<StarHalfIcon key="half" width={17.8} height={17.8} />);
    }

    // Render empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <StarEmptyIcon key={`empty-${i}`} width={17.8} height={17.8} />,
      );
    }

    return stars;
  };

  const hasReviews = rating > 0;

  return (
    <div className="mt-[11px] w-full flex flex-col items-center">
      <div className="flex gap-3 items-center justify-end sm:justify-center">
        {hasReviews && (
          <p className="hidden sm:inline-block font-monaMedium text-lg text-[#853A1B]">
            {rating.toFixed(1)}
          </p>
        )}
        <div className="flex gap-1 sm:gap-[6px]">{renderStars()}</div>
      </div>
      <p
        className={`mt-3 font-monaMedium text-sm ${
          hasReviews ? "text-[#1E54B7]" : "text-[#6F7782]"
        } hidden lg:inline-block`}
      >
        {hasReviews ? `${reviewCount} reviews` : "has no reviews yet"}
      </p>
      <p
        className={`mt-1 font-monaMedium text-sm ${
          hasReviews ? "text-[#1E54B7]" : "text-[#6F7782]"
        } lg:hidden`}
      >
        {hasReviews ? `${reviewCount} reviews` : "has no reviews yet"}
      </p>
    </div>
  );
}

function SocialIcons({
  socials,
}: {
  socials: { platform: string; url: string }[];
}) {
  const getSocialIcon = (platform: string) => {
    const platformLower = platform.toLowerCase();

    switch (platformLower) {
      case "linkedin":
        return <LinkedInFilledIcon />;
      case "facebook":
        return <FacebookFilledIcon />;
      case "instagram":
        return <InstagramFilledIcon />;
      case "youtube":
        return <YoutubeFilledIcon />;
      case "twitter":
      case "x":
        return <XTwitterIcon />;
      case "website":
      case "globe":
      default:
        return <GlobeFilledIcon />;
    }
  };

  if (!socials || socials.length === 0) {
    return null;
  }

  const orderedSocials = [...socials].sort((a, b) => {
    const aIsLinkedIn = a.platform.toLowerCase() === "linkedin" ? 0 : 1;
    const bIsLinkedIn = b.platform.toLowerCase() === "linkedin" ? 0 : 1;
    return aIsLinkedIn - bIsLinkedIn;
  });

  return (
    <div className="flex items-center gap-4">
      {orderedSocials.map((social, index) => (
        <a
          key={index}
          href={social.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Visit ${social.platform}`}
          className="hover:opacity-80 transition-opacity cursor-pointer"
        >
          {getSocialIcon(social.platform)}
        </a>
      ))}
    </div>
  );
}

function Socials({ platform, url }: { platform: string; url?: string }) {
  // Commented out icon mapping - keeping for future use if designer requires specific social icons
  // const getSocialIcon = (platform: string) => {
  //   const platformLower = platform.toLowerCase();

  //   switch (platformLower) {
  //     case "facebook":
  //       return <FacebookFilledIcon width={20} height={20} />;
  //     case "instagram":
  //       return <InstagramFilledIcon width={20} height={20} />;
  //     case "youtube":
  //       return <YoutubeFilledIcon width={20} height={20} />;
  //     case "twitter":
  //     case "x":
  //       return <XTwitterIcon width={20} height={20} />;
  //     case "linkedin":
  //       return <GlobeFilledIcon width={20} height={20} />; // Replace with LinkedIn icon when available
  //     case "medium":
  //       return <GlobeFilledIcon width={20} height={20} />; // Replace with Medium icon when available
  //     case "website":
  //     case "globe":
  //     default:
  //       return <GlobeFilledIcon width={20} height={20} />;
  //   }
  // };

  return (
    <a
      href={url || "#"}
      target={url ? "_blank" : undefined}
      rel={url ? "noopener noreferrer" : undefined}
      className="block w-full"
      aria-label={`Visit ${platform}`}
    >
      <button className="flex w-full gap-2 items-center justify-center bg-[#F2ECE2] py-2 rounded-lg font-monaMedium text-[#20242A] hover:bg-[#E3D9C8] transition-colors">
        {platform}
        <ArrowUpRightIcon stroke="currentColor" />
      </button>
    </a>
  );
}
